// src/pages/ResetPasswordPage.js
// eslint-disable-next-line no-unused-vars
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useLocation, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { MdLock } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import PageContentComponent from '../components/ui/pageContentComponent';
import PageContainerTitleComponent from '../components/ui/container/pageContainerTitleComponent';
import { resetPassword } from '../api/axios';
import i18n from '../i18n'; // Importiere deine i18n-Instanz

// Styled Components
const Container = styled.div`
  max-width: 400px;
  margin: 20px auto;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  text-align: center;
`;

const InputWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const IconWrapper = styled.div`
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #ccc;
  font-size: 1.2rem;
`;

const Input = styled.input`
  padding: 10px 10px 10px 40px;
  font-size: 1rem;
  border: 1px solid #ccc;
  border-radius: 4px;
  width: 100%;
`;

const Button = styled.button`
  padding: 10px;
  font-size: 1rem;
  background-color: #28a745;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  margin: 0 auto;
  &:disabled {
    background-color: #6c757d;
    cursor: not-allowed;
  }
`;

const Message = styled.p`
  text-align: center;
  color: ${props => (props.error ? 'red' : 'green')};
`;

const InfoText = styled.p`
  text-align: center;
  font-size: 0.9rem;
  color: #555;
`;

// Yup-Schema mit i18next für Fehlermeldungen
const schema = yup.object().shape({
  password: yup
    .string()
    .min(6, i18n.t('resetPasswordPage.passwordMin'))
    .max(22, i18n.t('resetPasswordPage.passwordMax'))
    .required(i18n.t('resetPasswordPage.passwordRequired')),
  password_confirm: yup
    .string()
    .oneOf([yup.ref('password'), null], i18n.t('resetPasswordPage.passwordsDoNotMatch'))
    .required(i18n.t('resetPasswordPage.confirmPasswordRequired')),
});

const ResetPasswordPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const token = queryParams.get('token') || '';

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm({
    resolver: yupResolver(schema),
  });

  const [feedback, setFeedback] = useState("");
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    if (!token) {
      navigate('/');
    }
  }, [token, navigate]);

  const onSubmit = async (data) => {
    try {
      const response = await resetPassword({
        token,
        password: data.password,
        password_confirm: data.password_confirm,
      });
      if (response.data && response.data.success) {
        setFeedback(t('resetPasswordPage.successFeedback'));
        setIsError(false);
        setTimeout(() => {
          navigate('/');
        }, 3000);
      } else {
        setFeedback(
          response.data && response.data.error
            ? response.data.error
            : t('resetPasswordPage.genericError')
        );
        setIsError(true);
        setTimeout(() => {
          navigate('/');
        }, 2000);
      }
    } catch (error) {
      setFeedback(
        error.response?.data?.error || t('resetPasswordPage.genericError')
      );
      setIsError(true);
      setTimeout(() => {
        navigate('/');
      }, 2000);
    }
    reset();
  };

  return (
    <PageContentComponent>
      <PageContainerTitleComponent title={t('resetPasswordPage.pageTitle')} />
      <Container>
        <h2>{t('resetPasswordPage.header')}</h2>
        <InfoText>{t('resetPasswordPage.infoText')}</InfoText>
        {token && (
          <form onSubmit={handleSubmit(onSubmit)}>
            <InputWrapper>
              <IconWrapper>
                <MdLock />
              </IconWrapper>
              <Input
                type="password"
                placeholder={t('resetPasswordPage.newPasswordPlaceholder')}
                {...register("password")}
              />
            </InputWrapper>
            {errors.password && <Message error>{errors.password.message}</Message>}
            <InputWrapper>
              <IconWrapper>
                <MdLock />
              </IconWrapper>
              <Input
                type="password"
                placeholder={t('resetPasswordPage.confirmPasswordPlaceholder')}
                {...register("password_confirm")}
              />
            </InputWrapper>
            {errors.password_confirm && (
              <Message error>{errors.password_confirm.message}</Message>
            )}
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? t('resetPasswordPage.updating') : t('resetPasswordPage.updatePassword')}
            </Button>
          </form>
        )}
        {feedback && <Message error={isError}>{feedback}</Message>}
      </Container>
    </PageContentComponent>
  );
};

export default ResetPasswordPage;
